import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { industries } from "@/mocks/homeData";

const industriesRow1 = industries.slice(0, 6);
const industriesRow2 = industries.slice(6, 11);

function IndustryBadge({
  industry,
  t,
}: {
  industry: { id: string; titleKey: string; icon: string };
  t: (key: string) => string;
}) {
  return (
    <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md mx-2 md:mx-3 flex-shrink-0 select-none">
      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/12 text-primary-300">
        <i className={`${industry.icon} text-base`} />
      </div>
      <span className="font-heading font-semibold text-sm text-foreground-100 whitespace-nowrap">
        {t(industry.titleKey)}
      </span>
    </div>
  );
}

export default function Industries() {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="industries"
      ref={sectionRef}
      className="relative py-10 md:py-14 overflow-hidden bg-background-950"
    >
      {/* Deep dark background with human photo subtly beneath */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Diverse%20group%20of%20industry%20professionals%20collaborating%20in%20a%20modern%20corporate%20lobby%2C%20warm%20handshake%20and%20teamwork%2C%20natural%20daylight%2C%20welcoming%20business%20atmosphere%2C%20editorial%20photography%2C%20human%20centered%2C%20warm%20neutral%20tones%20with%20subtle%20cool%20highlights&width=900&height=394&seq=industries-human-bg-01&orientation=landscape"
          srcSet="https://readdy.ai/api/search-image?query=Diverse%20group%20of%20industry%20professionals%20collaborating%20in%20a%20modern%20corporate%20lobby%2C%20warm%20handshake%20and%20teamwork%2C%20natural%20daylight%2C%20welcoming%20business%20atmosphere%2C%20editorial%20photography%2C%20human%20centered%2C%20warm%20neutral%20tones%20with%20subtle%20cool%20highlights%20900wquery=Diverse%20group%20of%20industry%20professionals%20collaborating%20in%20a%20modern%20corporate%20lobby%2C%20warm%20handshake%20and%20teamwork%2C%20natural%20daylight%2C%20welcoming%20business%20atmosphere%2C%20editorial%20photography%2C%20human%20centered%2C%20warm%20neutral%20tones%20with%20subtle%20cool%20highlights%20480w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=900&width=480&height=210&seq=industries-human-bg-01&orientation=landscape&height=394&seq=industries-human-bg-01&orientation=landscape"
          sizes="100vw"
          alt=""
          aria-hidden="true"
          width={900}
          height={394}
          className="w-full h-full object-cover object-center opacity-25"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-background-950/90" />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950 via-background-950/85 to-background-950" />
      </div>

      {/* Tech grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(oklch(var(--primary-500) / 0.4) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.4) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      {/* Radial glows */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[620px] h-[420px] bg-primary-500/8 rounded-full blur-[130px] pointer-events-none" />
      <div className="absolute bottom-0 right-[12%] w-[380px] h-[300px] bg-accent-500/6 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-6">
        {/* Title */}
        <div
          className={`text-center mb-6 md:mb-9 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white">
            {t("industries.title")} <span className="text-primary-400">{t("industries.highlight")}</span>
          </h2>
        </div>

        {/* Row 1 - scroll left */}
        <div
          className={`relative mb-4 transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-background-950 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-background-950 to-transparent z-10 pointer-events-none" />
          <div className="flex animate-scroll-left hover:[animation-play-state:paused]">
            {[...industriesRow1, ...industriesRow1].map((industry, idx) => (
              <IndustryBadge key={`r1-${idx}`} industry={industry} t={t} />
            ))}
          </div>
        </div>

        {/* Row 2 - scroll right */}
        <div
          className={`relative transition-all duration-1000 delay-400 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-background-950 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-background-950 to-transparent z-10 pointer-events-none" />
          <div className="flex animate-scroll-right hover:[animation-play-state:paused]">
            {[...industriesRow2, ...industriesRow2].map((industry, idx) => (
              <IndustryBadge key={`r2-${idx}`} industry={industry} t={t} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}